// Common constants
export const API_VERSION = 'v1';
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

export const ORDER_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

export const SHIPMENT_STATUS = {
  PLANNED: 'planned',
  ASSIGNED: 'assigned',
  IN_TRANSIT: 'in_transit',
  DELIVERED: 'delivered',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

// ===== Regional Options (USA-First) =====
// Common option type used across the application
export interface SelectOption {
  value: string;
  label: string;
}

// Timezone options (USA-first, with common international timezones)
export const TIMEZONE_OPTIONS: SelectOption[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/Istanbul', label: 'Turkey Time (TRT)' },
  { value: 'Europe/London', label: 'UK Time (GMT/BST)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'UTC', label: 'UTC' },
];

// Currency options (USA-first)
export const CURRENCY_OPTIONS: SelectOption[] = [
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'TRY', label: 'Turkish Lira (TRY)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
  { value: 'CAD', label: 'Canadian Dollar (CAD)' },
  { value: 'MXN', label: 'Mexican Peso (MXN)' },
];

const CURRENCY_LABELS_TR: Record<string, string> = {
  USD: 'Amerikan Doları (USD)',
  EUR: 'Avro (EUR)',
  TRY: 'Türk Lirası (TRY)',
  GBP: 'İngiliz Sterlini (GBP)',
  CAD: 'Kanada Doları (CAD)',
  MXN: 'Meksika Pesosu (MXN)',
};

/** Locale-aware currency options */
export function getCurrencyOptions(locale?: string): SelectOption[] {
  if (locale === 'tr') {
    return CURRENCY_OPTIONS.map((opt) => ({
      value: opt.value,
      label: CURRENCY_LABELS_TR[opt.value] || opt.label,
    }));
  }
  return CURRENCY_OPTIONS;
}

// Language options
export const LANGUAGE_OPTIONS: SelectOption[] = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'tr', label: 'Turkish' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
];

const LANGUAGE_LABELS_TR: Record<string, string> = {
  'en-US': 'İngilizce (ABD)',
  'en-GB': 'İngilizce (İngiltere)',
  tr: 'Türkçe',
  de: 'Almanca',
  es: 'İspanyolca',
};

/** Locale-aware language options */
export function getLanguageOptions(locale?: string): SelectOption[] {
  if (locale === 'tr') {
    return LANGUAGE_OPTIONS.map((opt) => ({
      value: opt.value,
      label: LANGUAGE_LABELS_TR[opt.value] || opt.label,
    }));
  }
  return LANGUAGE_OPTIONS;
}

// Country options (USA-first, with operational countries)
export const COUNTRY_OPTIONS: SelectOption[] = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'MX', label: 'Mexico' },
  { value: 'TR', label: 'Turkey' },
  { value: 'DE', label: 'Germany' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'FR', label: 'France' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'NL', label: 'Netherlands' },
];

// Default values for regional settings (USA-First)
export const DEFAULT_REGIONAL_SETTINGS = {
  timezone: 'America/New_York',
  currency: 'USD',
  language: 'en-US',
  country: 'US',
} as const;

// Status color maps (DRY-1: centralized for TMS + WMS)
export * from './status-colors';

// WMS Layout Designer constants
export * from './layout-designer.constants';

// Product rules constants
export * from './product-rules.constants';

// Zone scoring constants
export * from './zone-scoring.constants';

// Entity validation bounds (shared between backend DTOs + frontend zod)
export * from './entity-validation.constants';

// Zone types blocked from picking/allocation/replenishment sourcing
export * from './pick-block.constants';
